<script setup>
import QRCode from '@chenfengyuan/vue-qrcode'
import { cookieRef } from '@/@layouts/stores/config'

definePage({
  meta: {
    name: 'utility-qrcode',
    subject: 'feedback.menu',
    action: 'qr_code_generator',
  },
})

const avatarLocal = ref(null)
const refInputEl = ref()
const size = ref(250)

const changeAvatar = file => {
  const fileReader = new FileReader()
  const { files } = file.target
  if (files && files.length) {
    fileReader.readAsDataURL(files[0])
    fileReader.onload = () => {
      if (typeof fileReader.result === 'string')
        avatarLocal.value = fileReader.result
      const canvas = document.querySelector('canvas')

      onReady(canvas)
    }
  }
}

const qrcodeLink = ref(window.location.origin +'/feedback-collect')

const unit = cookieRef('selectedUnit')
const sourceType = ref()
const location = ref()
const formType = ref()


// watch location and formType and add into qrcodeLink
watch([location, formType, sourceType, unit], ([location, formType]) => {
  let url = window.location.origin +'/feedback-collect?'

  if(unit.value){
    url += `&u=${btoa(unit.value?.id ?? unit.value)}`
  }

  if(sourceType.value){
    url += `&s=${btoa(sourceType.value?.id ?? sourceType.value)}`
  }
  if (location){
    url += `&l=${btoa(location?.id)}`
  }
  if (formType){
    url += `&f=${btoa(formType?.id)}`
  }
  qrcodeLink.value = url
}, { immediate: true })

const {  data: sourceTypes } = await useApi(createUrl('masters/feedback-sources',
  {
    query: {
      select: ['id', 'name'],
      search: {
        source_type: "QrCode",
      },
    },
  }))

if(sourceTypes.value && sourceTypes.value.length ==1){
  sourceType.value = sourceTypes.value[0].id
}

const locationType = ref()

const { data: locationTypes } = await useApi(createUrl('/masters/location-types', {
  query: {
    search: {
      unit_id: unit,
    },
    select: ['id', 'name'],
  },

}), {
  beforeFetch: ({ url, options, cancel }) =>{
    if(!unit.value)
    {
      cancel()
    }
  },
})

const getSelectedLocationType = computed(() => {
  return locationType.value?.id??null
})

const { isFetching: locationLoading,  data: locations } =useApi(createUrl('/masters/locations', {
  query: {
    search: {
      location_type_id: getSelectedLocationType,
      unit_id: unit,
    },
    select: ['id', 'name', 'code'],
  },

}), {
  beforeFetch: ({ url, options, cancel }) =>{
    if(!(unit.value && locationType.value))
    {
      cancel()
    }
  },
})

const { isFetching: isFetchingFormTypes,  data: formTypes } =useListFetchingService('/masters/form-types', {
  query: {
    select: ['id', 'name'],
    unit_id: unit,
  },

})


const onReady=canvas=>{
  const context = canvas.getContext('2d')
  const image = new Image()
  if (!unit.value) return

  // Add form type name to the qr code at the bottom
  context.font = 'bold 15px Arial'
  context.fillStyle = '#000'
  context.textAlign = 'center'

  // wrap to second line if location is selected
  let text = formType.value?.name ?? 'General'

  if(location.value){
    text = text+ ' - ' + location.value?.name
  }
  context.fillText(text, canvas.width / 2, canvas.height-2)

  image.src =  avatarLocal.value
  image.onload = () => {
    const coverage = 0.15
    const width = Math.round(canvas.width * coverage)
    const x = (canvas.width - width) / 2

    image.setAttribute('crossorigin', 'anonymous')

    drawImage(context, image, x, x, width, width)
  }
}

const  drawImage=(context, image, x, y, width, height, radius = 4) =>{
  context.shadowOffsetX = 0
  context.shadowOffsetY = 2
  context.shadowBlur = 4
  context.shadowColor = '#00000040'
  context.lineWidth = 8
  context.beginPath()
  context.moveTo(x + radius, y)
  context.arcTo(x + width, y, x + width, y + height, radius)
  context.arcTo(x + width, y + height, x, y + height, radius)
  context.arcTo(x, y + height, x, y, radius)
  context.arcTo(x, y, x + width, y, radius)
  context.closePath()
  context.strokeStyle = '#fff'
  context.stroke()
  context.clip()
  context.fillStyle = '#fff'
  context.fillRect(x, x, width, height)
  context.drawImage(image, x, x, width, height)
}

const downloadQRCode = () => {
  var link = document.createElement('a')

  link.download = downloadOptions.value.name + '.' + downloadOptions.value.extension
  link.href = document.querySelector('canvas').toDataURL()
  link.click()

}

const copyQRCodeLink = () => {
  navigator.clipboard.writeText(qrcodeLink.value)
}


const downloadOptions =computed(()=>{

  let filename =  formType.value?.name ?? 'General'
  if(location.value){
    filename = filename + ' - ' + locationType.value?.name + ' - ' + location.value?.name
  }

  return { name: filename, extension: 'png' }
})
</script>

<template>
  <div>
    <!-- Page Header -->
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <div class="text-h5">
          QR Code Generator
        </div>
        <span class="text-body-2 text-medium-emphasis">
          Generate QR codes for feedback collection
        </span>
      </div>

      <div class="d-flex gap-3">
        <VBtn
          variant="tonal"
          color="primary"
          prepend-icon="tabler-copy"
          @click="copyQRCodeLink"
        >
          Copy Link
        </VBtn>
        <VBtn
          color="primary"
          prepend-icon="tabler-download"
          @click="downloadQRCode"
        >
          Download QR
        </VBtn>
      </div>
    </div>

    <VRow>
      <!-- Configuration Panel -->
      <VCol
        cols="12"
        md="5"
      >
        <VCard
          variant="flat"
          class="border"
        >
          <VCardItem>
            <template #title>
              <span class="text-h6 font-weight-medium">Configuration</span>
            </template>
            <template #subtitle>
              <span class="text-body-2 text-medium-emphasis">Customize your QR code</span>
            </template>
          </VCardItem>

          <VDivider />

          <VCardText class="d-flex flex-column gap-4">
            <!-- Logo Upload -->
            <div>
              <VLabel class="mb-1 text-body-2 text-high-emphasis">
                Logo Image
              </VLabel>
              <div
                class="upload-zone rounded-lg cursor-pointer d-flex align-center gap-3 pa-3"
                @click="refInputEl.click()"
              >
                <VAvatar
                  v-if="avatarLocal"
                  :image="avatarLocal"
                  size="48"
                  rounded="lg"
                />
                <VAvatar
                  v-else
                  size="48"
                  rounded="lg"
                  color="primary"
                  variant="tonal"
                >
                  <VIcon
                    icon="tabler-photo-plus"
                    size="24"
                  />
                </VAvatar>
                <div>
                  <span class="text-body-2 font-weight-medium d-block">
                    {{ avatarLocal ? 'Change logo' : 'Add a logo' }}
                  </span>
                  <span class="text-caption text-medium-emphasis">
                    PNG, JPG up to 2MB
                  </span>
                </div>
              </div>
              <input
                ref="refInputEl"
                type="file"
                name="file"
                accept=".jpeg,.png,.jpg,GIF"
                hidden
                @input="changeAvatar"
              >
            </div>

            <!-- QR Code Type -->
            <AppAutocomplete
              v-if="sourceTypes?.length > 1"
              v-model="sourceType"
              :items="sourceTypes"
              :item-title="item => item.name"
              :item-value="item => item.id"
              eager
              required
              :rules="[requiredValidator]"
              return-object
              placeholder="Search QR code type"
              label="QR Code Type"
              :menu-props="{ maxHeight: '200px' }"
            />

            <!-- Form Type -->
            <AppCombobox
              v-model="formType"
              :disabled="!unit"
              label="Form Type"
              :items="formTypes"
              return-object
              clearable
              :loading="isFetchingFormTypes"
              :item-title="item => item.name"
              :item-value="item => item.id"
              persistent-hint
              placeholder="Select form type"
            />

            <!-- Location Type -->
            <AppSelect
              v-model="locationType"
              :disabled="!unit && !locationType"
              :items="locationTypes ?? []"
              :item-title="item => item.name"
              :item-value="item => item.id"
              eager
              clearable
              return-object
              placeholder="Select location type"
              label="Location Type"
              :menu-props="{ maxHeight: '200px' }"
            />

            <!-- Location -->
            <AppAutocomplete
              v-model="location"
              :disabled="!(unit && locationType)"
              :loading="locationLoading"
              :items="locations ?? []"
              :item-title="item => item.code ? `${item.name} (${item.code})` : item.name"
              :item-value="item => item.id"
              eager
              clearable
              return-object
              placeholder="Search location"
              label="Location"
              :menu-props="{ maxHeight: '200px' }"
            />

            <!-- QR Size Slider -->
            <div>
              <div class="d-flex align-center justify-space-between mb-1">
                <VLabel class="text-body-2 text-high-emphasis">
                  QR Size
                </VLabel>
                <VChip
                  size="small"
                  variant="tonal"
                  color="primary"
                  label
                >
                  {{ size }}px
                </VChip>
              </div>
              <VSlider
                v-model="size"
                thumb-label
                :max="550"
                :min="100"
                :step="50"
                color="primary"
                track-color="grey-lighten-3"
                style="margin-inline-start: 0px; margin-inline-end: 0px;"
              />
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- QR Code Preview Panel -->
      <VCol
        cols="12"
        md="7"
      >
        <VCard
          variant="flat"
          class="border"
        >
          <VCardItem>
            <template #title>
              <span class="text-h6 font-weight-medium">QR Preview</span>
            </template>
            <template #subtitle>
              <span class="text-body-2 text-medium-emphasis">Live preview of generated QR code</span>
            </template>
          </VCardItem>

          <VDivider />

          <VCardText>
            <div class="qr-preview-area d-flex flex-column align-center justify-center rounded-lg pa-8">
              <template v-if="unit">
                <QRCode
                  :value="qrcodeLink"
                  :options="{
                    errorCorrectionLevel: 'Q',
                    width: size,
                  }"
                  @ready="onReady"
                />
              </template>
              <div
                v-else
                class="text-center"
              >
                <VAvatar
                  size="64"
                  color="primary"
                  variant="tonal"
                  class="mb-3"
                >
                  <VIcon
                    icon="tabler-qrcode"
                    size="32"
                  />
                </VAvatar>
                <div class="text-body-1 font-weight-medium mb-1">
                  No QR Code Yet
                </div>
                <span class="text-body-2 text-medium-emphasis">
                  Select a unit to generate your QR code
                </span>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>

<style lang="scss" scoped>
.upload-zone {
  border: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.5);
    background: rgba(var(--v-theme-primary), 0.04);
  }
}

.qr-preview-area {
  background: rgba(var(--v-theme-primary), 0.02);
  border: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  min-block-size: 350px;
}
</style>
